import {groupBy} from 'lodash';
import React from 'react';
import Select from 'react-select';

import {recordEvent} from 'src/components/Analytics';
import {ButtonGroupSwitch} from 'src/components/ButtonGroupSwitch';
import {useTranslations} from 'src/components/TranslationsContext';
import {Project, Site} from 'src/lib/api/project';
import {LOCAL_SITE_PATTERN, createSiteGroupId} from 'src/lib/siteUtils';

export const SiteSelect: React.FC<{
  selectedSiteId: Site['id'];
  setSelectedSiteId: (siteId: Site['id']) => void;
  project: Project;
}> = ({selectedSiteId, setSelectedSiteId, project}) => {
  const groupedSites = groupBy(project.sites, (s) => createSiteGroupId(s.id));
  const selectedSite = project.sites?.find((site) => site.id === selectedSiteId);
  const selectedGroup = selectedSite ? groupedSites[createSiteGroupId(selectedSiteId)] : null;

  return (
    <div className="flex items-center">
      <Select
        // unfortunately to stay on top of other hover dropdowns, adding z here
        className="z-30  w-auto"
        styles={{
          control: () => ({display: 'inline-flex'}),
          valueContainer: (rest) => ({
            ...rest,
            padding: 0,
            fontSize: '1.25rem',
            lineHeight: '1.75rem',
          }),
          indicatorSeparator: () => ({display: 'none'}),
          menu: (rest) => ({...rest, width: 'auto'}),
        }}
        options={project.sites
          ?.filter(
            // show only sites if they are the only site in a group or the total
            (s) => {
              const group = groupedSites[createSiteGroupId(s.id)];
              return group.length === 1 || !LOCAL_SITE_PATTERN.test(s.id);
            }
          )
          .map((s) => {
            return {
              value: s.id,
              label: s.name,
            };
          })}
        value={{value: selectedSite?.id, label: selectedSite?.name}}
        onChange={(option) => {
          if (option?.value) {
            setSelectedSiteId(option?.value);
            recordEvent('site_selected', {
              siteId: option.value,
              siteName: option.label || '',
              projectId: project.id,
              method: 'dropdown',
            });
          }
        }}
        isSearchable={true}
        isLoading={!project}
        closeMenuOnSelect={false}
        name="siteSelect"
      />
      {selectedGroup && selectedGroup.length > 1 && (
        <TotalLocalSwitch
          group={selectedGroup}
          selectedSiteId={selectedSiteId}
          setSelectedSiteId={setSelectedSiteId}
        />
      )}
    </div>
  );
};

const TotalLocalSwitch = ({
  group,
  selectedSiteId,
  setSelectedSiteId,
}: {
  group: Site[];
  selectedSiteId: string;
  setSelectedSiteId: (siteId: string) => void;
}) => {
  const {t} = useTranslations();
  const isTotal = !LOCAL_SITE_PATTERN.test(selectedSiteId);
  const totalId = group.find((s) => !LOCAL_SITE_PATTERN.test(s.id))!.id;
  const localId = group.find((s) => LOCAL_SITE_PATTERN.test(s.id))!.id;
  return (
    <ButtonGroupSwitch
      options={[
        {label: t('totalFlows'), value: totalId},
        {label: t('localFlows'), value: localId},
      ]}
      selectedValue={isTotal ? totalId : localId}
      onChange={(value) => {
        setSelectedSiteId(value);
        const selectedSite = group.find((s) => s.id === value);
        recordEvent('site_selected', {
          siteId: value,
          siteName: selectedSite?.name || '',
          projectId: group[0]?.projectId || '',
          method: 'total_local_switch',
          isTotal: !LOCAL_SITE_PATTERN.test(value),
        });
      }}
    />
  );
};
